// P16a — Hook RBAC centralisé
// Matrice des permissions par rôle.
// Source de vérité unique pour les checks frontend.
//
// Rôles (cf. backend/app/enums/role.py) :
//   - tech       : Technicien — lecture seule
//   - sup        : Superviseur — lecture + écriture (interventions, DA, stocks)
//   - respo      : Responsable — tout + validation DA + suppression
//   - app_admin  : Admin système — tout (y compris users, audit, import)
//
// Permissions disponibles :
//   - "read"     : voir les pages non-admin
//   - "write"    : créer/éditer les entités métier
//   - "delete"   : supprimer définitivement
//   - "validate" : valider/annuler les DA
//   - "stats"    : accéder aux statistiques
//   - "admin"    : gérer users, audit, import, params admin
//
// Usage :
//   const canWrite = useCan("write");
//   const canDelete = useCan("delete");
//   if (canDelete) { ... }
import { useAuth } from "@/hooks/useAuth";

export type Permission = "read" | "write" | "delete" | "validate" | "stats" | "admin";

const MATRIX: Record<Permission, Array<"tech" | "sup" | "respo" | "app_admin">> = {
  // Lecture : tous les rôles connectés
  read: ["tech", "sup", "respo", "app_admin"],
  // Écriture : sup et au-dessus
  write: ["sup", "respo", "app_admin"],
  // Suppression définitive : respo+
  delete: ["respo", "app_admin"],
  // Validation DA : respo+ (workflow officiel)
  validate: ["respo", "app_admin"],
  // Statistiques : respo+
  stats: ["respo", "app_admin"],
  // Admin (users, audit, import) : respo+
  admin: ["respo", "app_admin"],
};

export function useCan(permission: Permission): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return MATRIX[permission].includes(user.role);
}

// Helper pour checks multiples (AND) : utile pour "peut éditer ET valider"
export function useCanAll(permissions: Permission[]): boolean {
  return permissions.every((p) => useCan(p));
}

// Helper pour checks multiples (OR) : utile pour "peut éditer ou supprimer"
export function useCanAny(permissions: Permission[]): boolean {
  return permissions.some((p) => useCan(p));
}

// Export de la matrice pour affichage éventuel dans la page profil
export { MATRIX };
